import { useState, useEffect } from 'react';
import { Loader2, BarChart3 } from 'lucide-react';

const REGION_LABELS = { ALL: 'All Regions', US: 'United States', GB: 'United Kingdom', IN: 'India', GLOBAL: 'Global / Untagged' };
const TAB_COLORS = { news: '#EF4444', investor: '#3B82F6', reseller: '#F97316' };
const TAB_LABELS = { news: 'News', investor: 'Investor', reseller: 'Reseller' };
const ALL_TABS = ['news', 'investor', 'reseller'];

function formatCat(cat) {
  return (cat || 'unknown').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function heatColor(count) {
  if (count > 20) return '#EF4444';
  if (count > 10) return '#F97316';
  if (count > 5) return '#EAB308';
  if (count > 0) return '#9CA3AF';
  return null;
}

export function DataVolumetricsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeRegion, setActiveRegion] = useState('ALL');
  const [activeSection, setActiveSection] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('wtf_admin_token');
        const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/volumetrics`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(await resp.json());
      } catch (e) {
        console.error('Volumetrics load failed:', e);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-gray-400" size={24} /></div>;
  if (!data) return <div className="text-center text-gray-500 py-8">Failed to load</div>;

  const regions = data.regions || [];
  const regionData = data.by_region?.[activeRegion];
  const totals = regionData?.totals || {};
  const rows = regionData?.rows || [];

  const activeTabs = activeSection === 'all' ? ALL_TABS : [activeSection];

  // Tree cascade: filters only flow downward.
  //   Level 1 (Country): ALWAYS shows the full grand total per region — unaffected by section selection.
  //   Level 2 (Section): scoped to the selected country.
  //   Level 3 (Category matrix): scoped to country + section.
  const sectionTileCount = (tab) => {
    const rt = data.by_region?.[activeRegion]?.totals || {};
    if (tab === 'all') return rt.grand ?? 0;
    return rt[tab] ?? 0;
  };

  const countryTileCount = (r) => {
    // Always full grand total — country is the TOP of the tree, never narrowed by section.
    return data.by_region?.[r]?.totals?.grand ?? 0;
  };

  const comboIsEmpty = sectionTileCount(activeSection) === 0;

  // Matrix rows: from the selected region's rows, filtered to active columns.
  const filteredRows = rows
    .filter(row => activeTabs.some(t => (row[t] || 0) > 0))
    .map(row => ({
      ...row,
      _rowTotal: activeTabs.reduce((s, t) => s + (row[t] || 0), 0),
    }))
    .sort((a, b) => b._rowTotal - a._rowTotal);

  // Unified card styling:
  //   Selected → bold BLACK text on WHITE background
  //   Unselected/Disabled → bold WHITE text on DARK background, fully visible
  const cardStyle = (isActive, { disabled = false, accent = null } = {}) => ({
    background: isActive ? '#fff' : '#111827',
    color: isActive ? '#000' : '#fff',
    border: isActive
      ? '2px solid #fff'
      : `1px solid ${accent ? accent + '80' : 'rgba(255,255,255,0.15)'}`,
    cursor: disabled ? 'not-allowed' : 'pointer',
  });

  return (
    <div className="px-5 py-4 space-y-5 text-gray-100" data-testid="volumetrics-tab">

      {/* Grand totals bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <BarChart3 size={16} className="text-gray-400" />
        <span className="text-sm font-bold text-gray-200">Total Records:</span>
        <span className="text-lg font-black text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{data.grand_totals?.grand ?? 0}</span>
      </div>

      {/* Level 1: Country — counts cascade based on active section */}
      <div>
        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2 block">
          Country
        </span>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {regions.map(r => {
            const isActive = activeRegion === r;
            const cnt = countryTileCount(r);
            return (
              <button
                key={r}
                data-testid={`vol-region-${r}`}
                onClick={() => setActiveRegion(r)}
                className="rounded-xl p-3 text-center transition-all"
                style={cardStyle(isActive)}
              >
                <div className="text-2xl font-black" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {cnt}
                </div>
                <div className="text-xs font-black mt-0.5">{r}</div>
                <div className="text-[10px] font-semibold" style={{ opacity: 0.7 }}>{REGION_LABELS[r] || r}</div>
              </button>
            );
          })}
        </div>
        <p className="text-[10px] text-gray-500 mt-1.5 italic">
          Region inferred from source: News from country-specific ingestors; Investor / Reseller / TikTok from source_id region mapping. GLOBAL = crypto / forex / commodities / non-region-attributable.
        </p>
      </div>

      {/* Level 2: Section — counts cascade based on active country */}
      <div>
        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2 block">
          Section {activeRegion !== 'ALL' && <span className="normal-case font-normal">— within {activeRegion}</span>}
        </span>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {/* All sections */}
          <button
            data-testid="vol-section-all"
            onClick={() => setActiveSection('all')}
            className="rounded-xl p-2.5 text-center transition-all"
            style={cardStyle(activeSection === 'all')}
          >
            <div className="text-xl font-black" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {sectionTileCount('all')}
            </div>
            <div className="text-[9px] font-black uppercase tracking-wider mt-0.5">
              All Sections
            </div>
          </button>

          {ALL_TABS.map(t => {
            const isActive = activeSection === t;
            const accent = TAB_COLORS[t];
            const cnt = sectionTileCount(t);
            return (
              <button
                key={t}
                data-testid={`vol-section-${t}`}
                onClick={() => setActiveSection(t)}
                className="rounded-xl p-2.5 text-center transition-all"
                style={cardStyle(isActive, { accent })}
              >
                <div className="text-xl font-black" style={{ fontFamily: 'JetBrains Mono, monospace', color: isActive ? '#000' : accent }}>
                  {cnt}
                </div>
                <div className="text-[9px] font-black uppercase tracking-wider mt-0.5" style={{ color: isActive ? '#000' : '#fff' }}>
                  {TAB_LABELS[t]}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Matrix table */}
      <div>
        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2 block">
          Category breakdown — {activeRegion} / {activeSection === 'all' ? 'All Sections' : TAB_LABELS[activeSection]}
        </span>
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-white/5 border-b border-white/10">
                <th className="text-left py-2.5 px-3 font-bold text-gray-400 sticky left-0 bg-[#1a1a1a]">Category</th>
                {activeTabs.map(t => (
                  <th key={t} className="text-center py-2.5 px-3 font-bold" style={{ color: TAB_COLORS[t] }}>
                    {TAB_LABELS[t]}
                  </th>
                ))}
                {activeTabs.length > 1 && (
                  <th className="text-center py-2.5 px-3 font-bold text-gray-200">Total</th>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={activeTabs.length + 2} className="text-center py-8 text-gray-500">
                    No {activeSection === 'all' ? 'data' : TAB_LABELS[activeSection] + ' records'} for {activeRegion}.
                  </td>
                </tr>
              ) : filteredRows.map(row => (
                <tr key={row.category} className="border-b border-white/5 hover:bg-white/5">
                  <td className="py-2 px-3 font-semibold text-gray-200 sticky left-0 bg-[#1a1a1a]">{formatCat(row.category)}</td>
                  {activeTabs.map(t => {
                    const val = row[t];
                    const bg = heatColor(val);
                    return (
                      <td key={t} className="text-center py-2 px-3">
                        {val > 0 ? (
                          <span className="inline-block min-w-[30px] px-2 py-0.5 rounded-md text-white font-bold text-[11px]"
                            style={{ background: bg }}>
                            {val}
                          </span>
                        ) : <span className="text-gray-600">-</span>}
                      </td>
                    );
                  })}
                  {activeTabs.length > 1 && (
                    <td className="text-center py-2 px-3 font-black text-gray-100">{row._rowTotal}</td>
                  )}
                </tr>
              ))}
              {!comboIsEmpty && filteredRows.length > 0 && (
                <tr className="bg-white/5 font-black border-t-2 border-white/20">
                  <td className="py-2.5 px-3 text-gray-200 sticky left-0 bg-[#1a1a1a]">TOTAL</td>
                  {activeTabs.map(t => (
                    <td key={t} className="text-center py-2.5 px-3" style={{ color: TAB_COLORS[t] }}>
                      {totals[t] || 0}
                    </td>
                  ))}
                  {activeTabs.length > 1 && (
                    <td className="text-center py-2.5 px-3 text-gray-100">
                      {activeTabs.reduce((s, t) => s + (totals[t] || 0), 0)}
                    </td>
                  )}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
